import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { graphql, compose } from 'react-apollo';
import gql from 'graphql-tag';
import { Row, Col, Tabs, Tab } from 'react-bootstrap';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import _ from 'lodash';
import { FaUser } from 'react-icons/fa';
import qs from 'qs';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import { connect } from 'react-redux';
import { change, untouch, stopSubmit } from 'redux-form';

import Loading from 'components/Loading';
import Filter from 'components/Filter';
import List from 'components/HoursList/List';
import validations from 'components/ReduxForm/validations';
import validationMessages from 'components/ReduxForm/messages';
import Alert, { alertOpts } from 'components/Alert';
import hoursEntryTypes from 'core/hoursEntryTypes';

import messages from './messages';
import EditUserMonthsForm from './EditUserMonthsForm';
import UserEditForm from './EditForm';
import TotalFilteredHours from '../../Filter/TotalFilteredHours';
// eslint-disable-next-line css-modules/no-unused-class
import layoutStyle from '../../../styles/base/layout.scss';
import {
  DATEONLY_STRING_FMT_DB,
  DATEONLY_STRING_FMT,
  dateOnlyToString,
  stringToDateOnly,
} from '../../../core/dateonly';
import AddOrUpdateHoursEntryForm, {
  getInitialValues as getAddOrUpdateHoursEntryFormInitialValues,
} from '../../HoursList/AddOrUpdateHoursEntryForm';
import {
  handleIssueInputChangeAsync,
  handleProjectSelect,
} from '../../HoursList/util';
import { getErrorsFromApollo } from '../../../core/errors/util';
import errorMessages from '../../../core/errors/messages';
import {
  adminAddHoursEntryIfNotExistMutation,
  adminAddHoursEntryMutation,
} from './adminAddHoursMutations';

class UserEdit extends React.Component {
  static contextTypes = {
    client: PropTypes.object.isRequired,
  };

  static propTypes = {
    userQuery: PropTypes.shape({
      loading: PropTypes.bool.isRequired,
      refetch: PropTypes.func.isRequired,
      user: PropTypes.shape({
        id: PropTypes.string.isRequired,
        username: PropTypes.string.isRequired,
        hoursEntries: PropTypes.array.isRequired,
        missingHoursAlert: PropTypes.bool.isRequired,
      }),
    }).isRequired,
    // eslint-disable-next-line react/no-unused-prop-types
    match: PropTypes.shape({
      params: PropTypes.shape({
        username: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
    updateUser: PropTypes.func.isRequired,
    updateUserMonths: PropTypes.func.isRequired,
    intl: intlShape.isRequired,
    oAuthProviderQuery: PropTypes.shape({
      loading: PropTypes.bool.isRequired,
      currentOAuthProvider: PropTypes.string,
    }).isRequired,
    maxBulkAdditionHoursQuery: PropTypes.shape({
      loading: PropTypes.bool.isRequired,
      maxBulkAdditionHours: PropTypes.number,
    }).isRequired,
    descriptionRequirementQuery: PropTypes.shape({
      loading: PropTypes.bool.isRequired,
      descriptionRequirement: PropTypes.string,
    }).isRequired,
    showHomeOfficeQuery: PropTypes.shape({
      loading: PropTypes.bool.isRequired,
      showHomeOffice: PropTypes.bool,
    }).isRequired,
    showWorkCategoryQuery: PropTypes.shape({
      loading: PropTypes.bool.isRequired,
      showWorkCategory: PropTypes.bool,
    }).isRequired,
    adminAddHoursEntry: PropTypes.func.isRequired,
    adminAddHoursEntryIfNotExist: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);

    this.handleTabSelect = this.handleTabSelect.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleMonthsSubmit = this.handleMonthsSubmit.bind(this);
    this.closeAlert = this.closeAlert.bind(this);
    this.applyFilters = this.applyFilters.bind(this);
    this.handleExport = this.handleExport.bind(this);
    this.handleHoursSubmit = this.handleHoursSubmit.bind(this);
    this.state = {
      filter: {
        from: moment()
          .startOf('month')
          .format(DATEONLY_STRING_FMT_DB),
        to: moment()
          .endOf('month')
          .format(DATEONLY_STRING_FMT_DB),
      },
      tabs: {
        activeKey: 'hoursList',
      },
      alert: {
        ...alertOpts.success,
      },
    };
  }

  handleTabSelect(key) {
    this.setState({ tabs: { activeKey: key } });
  }

  async handleSubmit(formValues) {
    try {
      const values = {
        ..._.omit(formValues, ['__typename']),
      };
      if (formValues.startDate) {
        values.startDate = stringToDateOnly(formValues.startDate);
      }
      await this.props.updateUser(values);
      this.setState({ alert: { show: true } });
    } catch (e) {
      this.setState({
        alert: {
          ...alertOpts.error,
          show: true,
          msg: e.message,
        },
      });
    }
  }

  async handleMonthsSubmit(formValues) {
    try {
      const payload = {
        id: formValues.id,
        months: formValues.months.map(month => _.omit(month, ['__typename'])),
      };
      await this.props.updateUserMonths(payload);
      this.setState({ alert: { show: true } });
    } catch (e) {
      this.setState({
        alert: {
          ...alertOpts.error,
          show: true,
          msg: e.message,
        },
      });
    }
  }

  closeAlert() {
    this.setState({ alert: { show: false } });
  }

  async applyFilters({ filter }) {
    this.setState({ filter });
    this.props.userQuery.refetch({
      filter,
    });
  }

  handleExport({ filter, format }) {
    const filterString = qs.stringify({ filter });
    const exportTab = window.open('', '_blank');
    const userId = this.props.userQuery.user.id;
    const exportUrl = `/export/user/${userId}?${filterString}&format=${format}`;
    exportTab.location.href = exportUrl;
    exportTab.focus();
  }

  async handleHoursSubmit(formValues) {
    this.setState({ formErrors: {} });
    const { issueValues, ...payload } = formValues;
    const {
      userQuery: { user: { projects } },
      showWorkCategory,
      dispatch,
    } = this.props;
    const projectOption = _.get(formValues, 'project.value');
    const projectAndCategory = projectOption.split('_');
    payload.project = projectAndCategory[0];
    if (projectAndCategory.length === 2) {
      payload.category = projectAndCategory[1];
    } else {
      payload.category = null;
    }

    if (_.get(formValues, 'type')) {
      payload.type = _.get(formValues, 'type').value;
    }

    if (_.get(formValues, 'activity')) {
      payload.activity = _.get(formValues, 'activity').value;
    }

    if (_.get(formValues, 'issue.value')) {
      const title = _.get(formValues, 'issue.label').split(
        `#${_.get(formValues, 'issue.value')} `,
      );
      const issue = {
        ticketId: _.get(formValues, 'issue.value'),
        title: title[1],
      };
      payload.issue = { ...issue };
    } else {
      payload.issue = null;
    }
    const { timeTo } = payload;
    delete payload.timeTo;
    payload.timeFrom = moment(payload.timeFrom.value, 'HH:mm');

    // format date
    if (_.get(formValues, 'date')) {
      payload.date = stringToDateOnly(formValues.date);
    }
    if (showWorkCategory) payload.workCategory = formValues.workCategory.id;
    else payload.workCategory = null;

    try {
      const userId = this.props.userQuery.user.id;
      // project category validation
      //   unfortunately cannot do this directly in the form, because
      //   redux-form's validation function has no access to the
      //   component's props
      if (
        !validations.projectCategoryRequired({
          projects,
          projectId: payload.project,
          category: payload.category,
        })
      ) {
        const errors = {
          project: (
            <FormattedMessage {...validationMessages.projectCategoryRequired} />
          ),
        };
        dispatch(stopSubmit('addOrUpdateHoursEntryForm', errors));
        this.setState({ formErrors: errors });
        return false;
      }
      if (
        payload.type === hoursEntryTypes.SICK ||
        payload.type === hoursEntryTypes.VACATION ||
        payload.type === hoursEntryTypes.TIME_OFF_IN_LIEU
      ) {
        const from = moment(payload.date, DATEONLY_STRING_FMT_DB);
        const to = moment(payload.dateTo, DATEONLY_STRING_FMT);
        const days = to.diff(from, 'days');
        for (let i = 0; i <= days; i += 1) {
          payload.timeFrom = moment('09:00', 'HH:mm');
          // eslint-disable-next-line no-await-in-loop
          await this.props.adminAddHoursEntryIfNotExist(
            userId,
            payload,
            this.state.filter,
          );
          payload.date = from.add(1, 'days').format(DATEONLY_STRING_FMT_DB);
        }
      } else {
        await this.props.adminAddHoursEntry(userId, payload, this.state.filter);
      }
      this.props.dispatch(
        change('addOrUpdateHoursEntryForm', 'timeFrom', {
          value: timeTo.value,
          label: timeTo.label,
        }),
      );
      if (
        payload.type !== hoursEntryTypes.SICK &&
        payload.type !== hoursEntryTypes.VACATION &&
        payload.type !== hoursEntryTypes.TIME_OFF_IN_LIEU
      ) {
        this.props.dispatch(untouch('addOrUpdateHoursEntryForm', 'hours'));
        this.props.dispatch(change('addOrUpdateHoursEntryForm', 'hours', 0));
      }
      this.setState({ serverValidation: null });
      return true;
    } catch (e) {
      const errors = getErrorsFromApollo(e);
      this.setState({
        serverValidation: this.props.intl.formatMessage(
          errorMessages[errors[0].message],
          errors[0].messageValues,
        ),
      });
      return false;
    }
  }

  render() {
    const {
      userQuery: { loading, user },
      intl,
      oAuthProviderQuery: { currentOAuthProvider },
    } = this.props;
    const { filter } = this.state;
    const { alert } = this.state;

    if (loading || !user) return <Loading />;

    return (
      <div>
        <div className="h3">
          <FaUser size={20} style={{ marginRight: '5px' }} />
          {user.username}
        </div>
        <Tabs
          id="user-edit-tabs"
          activeKey={this.state.tabs.activeKey}
          onSelect={this.handleTabSelect}
          animation={false}
        >
          <Tab
            eventKey="hoursList"
            title={intl.formatMessage(messages.hoursListTabTitle)}
          >
            <Row className={layoutStyle.noMargin} style={{ marginTop: '15px' }}>
              <Col xs={12} className={layoutStyle.noPadding}>
                <Filter
                  onFilterApply={this.applyFilters}
                  appliedValues={filter}
                  projects={user.projects}
                  onExport={this.handleExport}
                />
              </Col>
              <Col xs={12}>
                <TotalFilteredHours
                  filter={filter}
                  hoursEntries={this.props.userQuery.user.hoursEntries}
                />
              </Col>
              <Col xs={12} md={8} style={{ paddingTop: '20px' }}>
                <List user={user} filter={filter} editable />
              </Col>
              <Col
                xs={12}
                md={4}
                className="pull-right"
                style={{ paddingTop: '20px' }}
              >
                <AddOrUpdateHoursEntryForm
                  form="updateHoursEntryForm"
                  onSubmit={this.handleHoursSubmit}
                  currentOAuthProvider={currentOAuthProvider}
                  projects={user.projects}
                  handleProjectSelect={({ value, isCategory }) => {
                    handleProjectSelect(this, { value, isCategory });
                  }}
                  issues={this.state.issues || []}
                  onIssueInputChange={value => {
                    handleIssueInputChangeAsync(this, { value });
                  }}
                  maxBulkAdditionHours={
                    this.props.maxBulkAdditionHoursQuery.maxBulkAdditionHours
                  }
                  descriptionRequirement={
                    this.props.descriptionRequirementQuery
                      .descriptionRequirement
                  }
                  initialValues={getAddOrUpdateHoursEntryFormInitialValues()}
                  serverValidation={this.state.serverValidation}
                  showHomeOffice={this.props.showHomeOfficeQuery.showHomeOffice}
                  showWorkCategory={
                    this.props.showWorkCategoryQuery.showWorkCategory
                  }
                  errors={this.state.formErrors}
                />
              </Col>
            </Row>
          </Tab>
          <Tab
            eventKey="months"
            title={intl.formatMessage(messages.monthsTabTitle)}
          >
            <Row className={layoutStyle.noMargin} style={{ marginTop: '15px' }}>
              <Col xs={12} className={layoutStyle.noPadding}>
                <EditUserMonthsForm
                  initialValues={{
                    id: user.id,
                    months: [...user.months].sort(
                      (a, b) =>
                        new Date(a.start).getTime() >
                        new Date(b.start).getTime()
                          ? -1
                          : 1,
                    ),
                  }}
                  onSubmit={this.handleMonthsSubmit}
                />
              </Col>
            </Row>
          </Tab>
          <Tab
            eventKey="userData"
            title={intl.formatMessage(messages.userDataTabTitle)}
          >
            <Row className={layoutStyle.noMargin} style={{ marginTop: '15px' }}>
              <Col xs={12} className={layoutStyle.noPadding}>
                <UserEditForm
                  initialValues={{
                    id: user.id,
                    username: user.username,
                    weeklyHours: user.weeklyHours,
                    startDate: dateOnlyToString(user.startDate),
                    missingHoursAlert: user.missingHoursAlert,
                    bypassTimeRestrictions: user.bypassTimeRestrictions,
                  }}
                  onSubmit={this.handleSubmit}
                />
              </Col>
            </Row>
          </Tab>
        </Tabs>
        <Alert {...alert} onConfirm={this.closeAlert} />
      </div>
    );
  }
}

const userQuery = gql`
  query userQuery($username: String!, $filter: HoursEntryFilter) {
    user(username: $username) {
      id
      username
      weeklyHours
      startDate
      missingHoursAlert
      bypassTimeRestrictions
      projects {
        id
        name
        categories {
          id
          name
        }
      }
      months {
        id
        name
        weeklyHours
        start
      }
      hoursEntries(filter: $filter) {
        id
        date
        timeFrom
        hours
        type
        activity
        workCategory {
          id
          name
          color
        }
        project {
          id
          name
        }
        issue {
          id
          ticketId
          title
          linkUrl
        }
        category {
          id
          name
        }
        description
      }
      dailyBalance {
        total
        done
        balance
      }
      weeklyBalance(filter: $filter) {
        total
        done
        balance
      }
      monthlyBalance(filter: $filter) {
        total
        done
        balance
      }
      totalBalance(filter: $filter) {
        total
        done
        balance
      }
      vacationBalance(filter: $filter) {
        total
        done
        balance
      }
    }
  }
`;

const oAuthProviderQuery = gql`
  query oAuthProviderQuery {
    currentOAuthProvider
  }
`;

const maxBulkAdditionHoursQuery = gql`
  query maxBulkAdditionHoursQuery {
    maxBulkAdditionHours
  }
`;

const descriptionRequirementQuery = gql`
  query descriptionRequirementQuery {
    descriptionRequirement
  }
`;

const showHomeOfficeQuery = gql`
  query showHomeOfficeQuery {
    showHomeOffice
  }
`;

const showWorkCategoryQuery = gql`
  query showWorkCategoryQuery {
    showWorkCategory
  }
`;

const updateUserMutation = gql`
  mutation updateUser($user: UserInput!) {
    updateUser(user: $user) {
      id
      username
      active
      weeklyHours
      missingHoursAlert
    }
  }
`;

const updateUserMonthsMutation = gql`
  mutation updateUserMonths($user: UserInput!) {
    updateUserMonths(user: $user) {
      id
      months {
        id
        name
        weeklyHours
        start
      }
    }
  }
`;

export default compose(
  connect(),
  graphql(oAuthProviderQuery, {
    name: 'oAuthProviderQuery',
  }),
  graphql(userQuery, {
    name: 'userQuery',
    options: ({ match }) => ({
      variables: {
        username: match.params.username,
        filter: {
          from: moment()
            .startOf('month')
            .format(DATEONLY_STRING_FMT_DB),
          to: moment()
            .endOf('month')
            .format(DATEONLY_STRING_FMT_DB),
        },
      },
    }),
  }),
  graphql(maxBulkAdditionHoursQuery, {
    name: 'maxBulkAdditionHoursQuery',
  }),
  graphql(descriptionRequirementQuery, {
    name: 'descriptionRequirementQuery',
  }),
  graphql(showHomeOfficeQuery, {
    name: 'showHomeOfficeQuery',
  }),
  graphql(showWorkCategoryQuery, {
    name: 'showWorkCategoryQuery',
  }),
  graphql(updateUserMutation, {
    props: ({ mutate }) => ({
      updateUser: user =>
        mutate({
          variables: { user },
        }),
    }),
  }),
  graphql(updateUserMonthsMutation, {
    props: ({ mutate }) => ({
      updateUserMonths: user =>
        mutate({
          variables: { user },
        }),
    }),
  }),
  graphql(adminAddHoursEntryMutation, {
    props: ({ mutate }) => ({
      adminAddHoursEntry: (userId, hoursEntry, filter) =>
        mutate({
          variables: { userId, payload: { ...hoursEntry }, filter },
        }),
    }),
  }),
  graphql(adminAddHoursEntryIfNotExistMutation, {
    props: ({ mutate }) => ({
      adminAddHoursEntryIfNotExist: (userId, hoursEntry, filter) =>
        mutate({
          variables: { userId, payload: { ...hoursEntry }, filter },
        }),
    }),
  }),
)(injectIntl(withStyles(layoutStyle)(UserEdit)));
